/**
 * Calculate order taxes and totals when editing an order
 * Similar to CalculateTaxes.tsx in pennicart_sdk but adapted for order editing
 */

export interface OrderProduct {
  order_product_id: number;
  product_name: string;
  product_price: number;
  product_tax: number;
  product_discount: number;
  quantity: number;
  bundle_products?: any[];
  [key: string]: any;
}

export interface DiscriminatedTax {
  tax_rate: string;
  tax_type: string;
  tax_amount: string;
}

export interface OrderData {
  subtotal_price: string | number;
  total_price: string | number;
  total_discount: string | number;
  total_tax: string | number;
  shipping_price: string | number;
  discriminated_taxes?: DiscriminatedTax[];
  tax_included?: boolean;
  [key: string]: any;
}

export interface CalculatedOrderResult {
  newSubtotal: number;
  newTotalTax: number;
  newTotal: number;
  originalTotal: number;
  difference: number;
  differenceType: 'collect' | 'refund' | 'none';
  discriminatedTaxes: DiscriminatedTax[];
  products: OrderProduct[];
}

/**
 * Calculate order totals based on updated product quantities
 * @param order - The original order data
 * @param products - The products array from the order
 * @param updatedQuantities - Object with product index as key and new quantity as value
 * @param deletedProducts - Object with product index as key and boolean indicating if deleted
 * @param taxIncluded - Whether tax is included in the product price (default: false)
 * @returns Calculated order result with new totals and difference
 */
export const calculateOrderTaxes = (
  order: OrderData,
  products: OrderProduct[],
  updatedQuantities: { [key: number]: number },
  deletedProducts: { [key: number]: boolean },
  taxIncluded: boolean = false
): CalculatedOrderResult => {
  const originalTotal = Number(order.total_price) || 0;
  const shippingPrice = Number(order.shipping_price) || 0;
  const totalDiscount = Number(order.total_discount) || 0;

  let newSubtotal = 0;
  let newTotalTax = 0;

  // Track tax amounts by type for discriminated taxes
  const taxByType: { [key: string]: { rate: number; amount: number } } = {};

  // Initialize tax types from original discriminated taxes if available
  if (order.discriminated_taxes && order.discriminated_taxes.length > 0) {
    order.discriminated_taxes.forEach((tax) => {
      const taxRate = parseFloat(tax.tax_rate.replace('%', ''));
      taxByType[tax.tax_type] = { rate: taxRate, amount: 0 };
    });
  }

  // Calculate totals for each product with updated quantities
  const updatedProducts = products.map((product, index) => {
    // If product is deleted, treat quantity as 0 for calculations
    const isDeleted = deletedProducts[index] || false;
    const quantity = isDeleted ? 0 : (updatedQuantities[index] !== undefined ? updatedQuantities[index] : product.quantity);
    const productPrice = Number(product.product_price) || 0;
    const productTaxRate = Number(product.product_tax) || 0;
    const productDiscount = Number(product.product_discount) || 0;

    let productSubtotal = 0;
    let productTax = 0;

    if (taxIncluded) {
      // If tax is included in the price, extract it
      const priceWithoutTax = Math.round((productPrice / (1 + productTaxRate / 100)) * 100) / 100;
      productSubtotal = priceWithoutTax * quantity;
      productTax = Math.round((productPrice - priceWithoutTax) * quantity * 100) / 100;
    } else {
      // Tax is not included, calculate it separately
      productSubtotal = productPrice * quantity;
      productTax = Math.round((productSubtotal * productTaxRate / 100) * 100) / 100;
    }

    // Apply per-product discount proportionally
    // Note: In this order editing context, we maintain the original discount structure
    
    newSubtotal += productSubtotal;
    newTotalTax += productTax;

    // Distribute tax to discriminated taxes if available
    if (order.discriminated_taxes && order.discriminated_taxes.length > 0) {
      // Calculate proportional tax for each tax type based on original ratios
      const originalTotalTaxRate = order.discriminated_taxes.reduce(
        (sum, tax) => sum + parseFloat(tax.tax_rate.replace('%', '')),
        0
      );

      if (originalTotalTaxRate > 0 && productTaxRate > 0) {
        order.discriminated_taxes.forEach((tax) => {
          const taxRate = parseFloat(tax.tax_rate.replace('%', ''));
          const proportionalTax = Math.round(((taxRate / originalTotalTaxRate) * productTax) * 100) / 100;
          if (taxByType[tax.tax_type]) {
            taxByType[tax.tax_type].amount += proportionalTax;
          }
        });
      }
    }

    // Handle bundle products if any
    if (product.bundle_products && product.bundle_products.length > 0) {
      product.bundle_products.forEach((bundle: any) => {
        const bundlePrice = Number(bundle.price || bundle.original_price) || 0;
        const bundleQuantity = Number(bundle.quantity) || 1;

        let bundleTax = 0;
        let bundleSubtotal = 0;

        if (taxIncluded) {
          const bundlePriceWithoutTax = Math.round((bundlePrice / (1 + productTaxRate / 100)) * 100) / 100;
          bundleSubtotal = bundlePriceWithoutTax * bundleQuantity * quantity;
          bundleTax = Math.round((bundlePrice - bundlePriceWithoutTax) * bundleQuantity * quantity * 100) / 100;
        } else {
          bundleSubtotal = bundlePrice * bundleQuantity * quantity;
          bundleTax = Math.round((bundleSubtotal * productTaxRate / 100) * 100) / 100;
        }

        newSubtotal += bundleSubtotal;
        newTotalTax += bundleTax;
      });
    }

    return {
      ...product,
      quantity,
      calculated_subtotal: productSubtotal,
      calculated_tax: productTax,
    };
  });

  // Round final values
  newSubtotal = Math.round(newSubtotal * 100) / 100;
  newTotalTax = Math.round(newTotalTax * 100) / 100;

  // Calculate new total: subtotal + shipping + tax - discount
  // Note: discount is already applied to subtotal in most cases, but we keep it for reference
  const newTotal = Math.round((newSubtotal + shippingPrice + newTotalTax) * 100) / 100;

  // Calculate difference
  const difference = Math.round((newTotal - originalTotal) * 100) / 100;
  let differenceType: 'collect' | 'refund' | 'none' = 'none';

  if (difference > 0) {
    differenceType = 'collect';
  } else if (difference < 0) {
    differenceType = 'refund';
  }

  // Build discriminated taxes array
  const discriminatedTaxes: DiscriminatedTax[] = Object.entries(taxByType).map(([taxType, data]) => ({
    tax_type: taxType,
    tax_rate: `${data.rate.toFixed(2)}%`,
    tax_amount: data.amount.toFixed(2),
  }));

  return {
    newSubtotal,
    newTotalTax,
    newTotal,
    originalTotal,
    difference,
    differenceType,
    discriminatedTaxes,
    products: updatedProducts,
  };
};

/**
 * Format price with currency symbol and 2 decimal places
 * @param price - The price to format
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted price string
 */
export const formatOrderPrice = (price: number | string, currency: string = 'USD'): string => {
  const numPrice = Number(price) || 0;
  return numPrice.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
